import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import Hotkeys from 'react-hot-keys';

import { Slide, SlideType } from '../components/SlideComponents/slide.types';
import { LeaderBoard, Response } from '../services/backendService/types';
import { useMetrics } from './useMetrics';
import { useWindowSize } from './useWindowSize';

const useSwipeResponses = ({
  selectedResponse,
  slideId,
  setSelectedResponse,
  podiumResponses,
  gridResponses,
  allSlides,
  slideType,
}: {
  selectedResponse: Response | undefined;
  slideId: string | undefined;
  setSelectedResponse: Dispatch<SetStateAction<Response | undefined>>;
  podiumResponses?: LeaderBoard | undefined;
  gridResponses?: Response[] | undefined;
  allSlides?: Slide[];
  slideType?: SlideType;
}) => {
  const metrics = useMetrics();
  const [windowWidth] = useWindowSize();
  const [isSwipingRight, setIsSwipingRight] = useState<boolean>(false);
  const [isSwipingLeft, setIsSwipingLeft] = useState<boolean>(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  const responseRoundType = useMemo(() => {
    if (allSlides) {
      const currentSlide = allSlides.find(r => r.id === slideId);
      if (!currentSlide) return undefined;

      const relatedSlide = allSlides.findIndex(
        r => r.id === currentSlide.dataReferenceSlideId,
      );
      const foundSlideType = allSlides[relatedSlide - 1].type;
      return foundSlideType;
    }
    return slideType;
  }, [allSlides, slideId, slideType]);

  const handleSkipBackResponse = () => {
    //for podium view
    if (selectedResponse && podiumResponses) {
      const index = podiumResponses.findIndex(
        response => response.response.id === selectedResponse.id,
      );
      if (index <= 0) {
        setSelectedResponse(podiumResponses[podiumResponses.length - 1].response);
      } else {
        setSelectedResponse(podiumResponses[index - 1].response);
      }
    }
    //for grid view
    if (selectedResponse && gridResponses) {
      const index = gridResponses.findIndex(
        response => response.id === selectedResponse.id,
      );
      if (index <= 0) {
        setSelectedResponse(gridResponses[gridResponses.length - 1]);
      } else {
        setSelectedResponse(gridResponses[index - 1]);
      }
    }
  };

  const handleSkipForwardResponse = () => {
    //for podium view
    if (selectedResponse && podiumResponses) {
      const index = podiumResponses.findIndex(
        response => response.response.id === selectedResponse.id,
      );
      if (index + 1 === podiumResponses.length) {
        setSelectedResponse(podiumResponses[0].response);
      } else {
        setSelectedResponse(podiumResponses[index + 1].response);
      }
    }
    //for grid view
    if (selectedResponse && gridResponses) {
      const index = gridResponses.findIndex(
        response => response.id === selectedResponse.id,
      );
      if (index + 1 === gridResponses.length) {
        setSelectedResponse(gridResponses[0]);
      } else {
        setSelectedResponse(gridResponses[index + 1]);
      }
    }
  };

  const HotkeySwipeRight = () => {
    return <Hotkeys keyName="right" onKeyUp={handleSkipForwardResponse} />;
  };

  const HotkeySwipeLeft = () => {
    return <Hotkeys keyName="left" onKeyUp={handleSkipBackResponse} />;
  };

  useEffect(() => {
    if (touchEndX !== touchStartX) {
      const swipeLength = (windowWidth / 100) * 10; //swipe must be 10% of screen width

      if (window && window.getSelection()) {
        const selectedText = window.getSelection()?.toString();
        if (selectedText && selectedText.length > 0) {
          // If text is selected within a response, do not proceed with swiping
          return;
        }
      }

      if (touchStartX > touchEndX + swipeLength) {
        setIsSwipingLeft(true);
        handleSkipForwardResponse();
        metrics.logEvent('SwipeResponse.SkipForward.SwipedLeft', undefined);
        setIsSwipingLeft(false);
      }
      if (touchEndX > touchStartX + swipeLength) {
        setIsSwipingRight(true);
        handleSkipBackResponse();
        metrics.logEvent('SwipeResponse.SkipBack.SwipedRight', undefined);
        setIsSwipingRight(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchEndX]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMouseDown = (e: any) => {
    if (selectedResponse !== undefined) {
      if (e.changedTouches || e.clientX) {
        setTouchStartX(e.changedTouches ? e.changedTouches[0].screenX : e.clientX);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMouseUp = (e: any) => {
    if (selectedResponse !== undefined) {
      if (e.changedTouches || e.clientX) {
        setTouchEndX(e.changedTouches ? e.changedTouches[0].screenX : e.clientX);
      }
    }
  };

  useEffect(() => {
    if (selectedResponse !== undefined) {
      window.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchstart', handleMouseDown);
      window.addEventListener('touchend', handleMouseUp);

      return () => {
        window.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchstart', handleMouseDown);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResponse]);

  return {
    responseRoundType,
    handleSkipBackResponse,
    handleSkipForwardResponse,
    HotkeySwipeRight,
    HotkeySwipeLeft,
    isSwipingRight,
    isSwipingLeft,
  };
};

export default useSwipeResponses;
